import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  AfterViewInit,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  booleanAttribute,
  inject,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { DsAvatarComponent } from './ds-avatar.component';
import {
  DsHeaderButtonComponent,
  type DsHeaderButtonNotification,
  type DsHeaderButtonState,
} from './ds-header-button.component';
import { DsLogoComponent, type DsLogoSize } from './ds-logo.component';
import { DsIconButtonV2Component } from '../buttons/ds-icon-button-v2.component';
import { DsContentSheetComponent } from '../containment/ds-content-sheet.component';
import { DsScrollComponent } from '../styles/ds-scroll';
import {
  DsInputSearchComponent,
  type DsInputSearchResult,
} from '../inputs/ds-input-search.component';
import {
  DsHeaderContactContentDirective,
  DsHeaderContactFooterDirective,
  DsHeaderMobileMenuDirective,
  type DsHeaderMobileMenuTemplateContext,
  type DsHeaderLegacyPanelTemplateContext,
  type DsHeaderManagedPanelTemplateContext,
  type DsHeaderPanelAction,
  DsHeaderPanelContentDirective,
  DsHeaderPanelDirective,
  DsHeaderPanelFooterDirective,
  DsHeaderPanelHeaderDirective,
  type DsHeaderPanelTemplateContext,
  DsHeaderProfileContentDirective,
  DsHeaderProfileFooterDirective,
} from './ds-header-templates.directive';

/** @deprecated Use the application-defined item value instead. */
export type DsHeaderAction = string;

export type DsHeaderItemVisual = 'icon' | 'avatar';

/** Action rendered by Header. Its value is defined entirely by the application. */
export interface DsHeaderItem {
  value: string;
  label: string;
  ariaLabel?: string;
  icon?: string;
  visual?: DsHeaderItemVisual;
  initials?: string;
  notification?: DsHeaderButtonNotification;
  notificationCount?: number;
  notificationAriaLabel?: string;
  disabled?: boolean;
  hideOnMobile?: boolean;
  opensPanel?: boolean;
  panelTitle?: string;
  panelAriaLabel?: string;
  panelMessage?: string;
  panelMaxWidth?: number | string;
}

export interface DsHeaderItemSelection {
  item: DsHeaderItem;
  value: string;
  event: MouseEvent;
}

@Component({
  selector: 'ds-header',
  standalone: true,
  imports: [
    CdkTrapFocus,
    NgTemplateOutlet,
    DsIconButtonV2Component,
    DsContentSheetComponent,
    DsScrollComponent,
    DsAvatarComponent,
    DsLogoComponent,
    DsHeaderButtonComponent,
    DsInputSearchComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header
      #headerElement
      class="ds-header"
      [class.ds-header--panel-open]="isPanelOpen"
    >
      <ds-logo
        class="ds-header__logo"
        [size]="logoSize"
        [invert]="logoInvert"
        [href]="logoHref"
        [target]="logoTarget"
        [rel]="logoRel"
        [ariaLabel]="logoAlt"
        [actionable]="true"
        (clicked)="logoClicked.emit($event)"
      />

      <div class="ds-header__tools">
        @if (showSearch) {
          <div class="ds-header__search" role="search">
            <ds-input-search
              class="ds-header__search-component"
              [value]="searchValue"
              [expanded]="searchExpanded"
              label=""
              [ariaLabel]="searchAriaLabel"
              [placeholder]="searchPlaceholder"
              [suggestions]="searchSuggestions"
              [results]="searchResults"
              [showNoResults]="searchShowNoResults"
              [maxCharacters]="searchMaxCharacters"
              [showMaxCharacters]="false"
              [disabled]="searchDisabled"
              [clearLabel]="searchClearLabel"
              [suggestionsLabel]="searchSuggestionsLabel"
              [emptyTitle]="searchEmptyTitle"
              [emptyMessage]="searchEmptyMessage"
              (valueChange)="onSearchValueChange($event)"
              (expandedChange)="onSearchExpandedChange($event)"
              (submitted)="onSearchSubmit($event)"
              (cleared)="searchCleared.emit()"
              (suggestionSelected)="searchSuggestionSelected.emit($event)"
              (resultSelected)="searchResultSelected.emit($event)"
            />
          </div>
        }

        <nav class="ds-header__actions" [attr.aria-label]="actionsAriaLabel">
          @if (showSearch) {
            <ds-header-button
              #mobileSearchAnchor
              class="ds-header__mobile-only ds-header__mobile-search"
              [label]="searchAriaLabel"
              [ariaLabel]="searchAriaLabel"
              icon="Search"
              size="s"
              [iconOnly]="true"
              [state]="actionState('search')"
              (clicked)="onSystemActionClicked('search', $event)"
            />
          }

          @for (item of resolvedItems; track item.value) {
            <ds-header-button
              #itemAnchor
              class="ds-header__responsive-action"
              [class.ds-header__contact-action]="item.value === 'contact'"
              [class.ds-header__notifications-action]="item.value === 'notifications'"
              [class.ds-header__profile-action]="item.value === 'profile'"
              [class.ds-header__desktop-only-action]="item.hideOnMobile"
              [label]="item.label"
              [ariaLabel]="item.ariaLabel || item.label"
              [icon]="item.visual === 'avatar' ? '' : (item.icon || '')"
              [notification]="item.notification || 'none'"
              [notificationCount]="item.notificationCount ?? 0"
              [notificationAriaLabel]="item.notificationAriaLabel || ''"
              [disabled]="item.disabled || false"
              [state]="actionState(item.value)"
              (clicked)="onItemClicked(item, $event)"
            >
              @if (item.visual === 'avatar') {
                <ds-avatar
                  dsItemButtonVisual
                  [initials]="item.initials || ''"
                  size="s"
                  [decorative]="true"
                />
              }
            </ds-header-button>
          }

          @if (showMenu) {
            <ds-header-button
              #mobileMenuAnchor
              class="ds-header__mobile-only ds-header__mobile-menu"
              [label]="menuLabel"
              [ariaLabel]="menuAriaLabel"
              icon="Menu"
              size="m"
              [iconOnly]="true"
              [state]="actionState('menu')"
              (clicked)="onSystemActionClicked('menu', $event)"
            />
          }
        </nav>
      </div>
    </header>

    @if (showSearch && searchExpanded) {
      <div
        class="ds-header__search-backdrop"
        aria-hidden="true"
        (click)="onSearchExpandedChange(false)"
      ></div>
    }

    @for (item of resolvedItems; track item.value) {
      @if (item.opensPanel !== false) {
        <ds-content-sheet
          class="ds-header__item-sheet"
          [class.ds-header__contact-sheet]="item.value === 'contact'"
          [class.ds-header__notifications-sheet]="item.value === 'notifications'"
          [class.ds-header__profile-sheet]="item.value === 'profile'"
          [class.ds-header__item-sheet--desktop-only]="item.hideOnMobile"
          [attr.data-header-item]="item.value"
          [visible]="!hasProjectedPanel(item.value) && openAction === item.value"
          [title]="managedPanelTitle(item.value, item.panelTitle || item.label)"
          [descriptiveText]="managedPanelDescriptiveText(item.value)"
          [ariaLabel]="managedPanelAriaLabel(item.value, item.panelAriaLabel || item.ariaLabel || item.label)"
          [anchor]="panelAnchorElement(item.value)"
          [overlayBoundary]="headerBoundaryElement"
          [maxWidth]="managedPanelMaxWidth(item.value, item.panelMaxWidth || contentSheetDefaultWidth)"
          [dismissOnBackdropClick]="managedPanelDismissOnBackdropClick(item.value)"
          [showCloseButton]="managedPanelShowCloseButton(item.value)"
          [showBackButton]="managedPanelShowBackButton(item.value)"
          [truncateTitle]="managedPanelTruncateTitle(item.value)"
          [shadow]="managedPanelShadow(item.value)"
          [closeAriaLabel]="managedPanelCloseAriaLabel(item.value)"
          [backAriaLabel]="managedPanelBackAriaLabel(item.value)"
          (dismissed)="closeManagedPanel(item.value, true)"
          (backed)="emitManagedPanelBacked(item.value)"
        >
          @if (managedPanelHeaderFor(item.value); as headerTemplate) {
            <div ds-content-sheet-header-navigation>
              <ng-container
                [ngTemplateOutlet]="headerTemplate.templateRef"
                [ngTemplateOutletContext]="managedPanelContext(item.value)"
              />
            </div>
          }

          @if (managedPanelContentFor(item.value); as contentTemplate) {
            <ng-container
              [ngTemplateOutlet]="contentTemplate.templateRef"
              [ngTemplateOutletContext]="managedPanelContext(item.value)"
            />
          } @else {
            @if (legacyContentTemplateFor(item.value); as legacyContentTemplate) {
              <ng-container
                [ngTemplateOutlet]="legacyContentTemplate.templateRef"
                [ngTemplateOutletContext]="panelTemplateContext"
              />
            } @else {
              <p class="ds-text-body-regular">{{ item.panelMessage || defaultPanelMessage }}</p>
            }
          }

          @if (managedPanelFooterFor(item.value); as footerTemplate) {
            <div ds-content-sheet-footer>
              <ng-container
                [ngTemplateOutlet]="footerTemplate.templateRef"
                [ngTemplateOutletContext]="managedPanelContext(item.value)"
              />
            </div>
          } @else {
            @if (legacyFooterTemplateFor(item.value); as legacyFooterTemplate) {
              <div ds-content-sheet-footer>
                <ng-container
                  [ngTemplateOutlet]="legacyFooterTemplate.templateRef"
                  [ngTemplateOutletContext]="panelTemplateContext"
                />
              </div>
            }
          }
        </ds-content-sheet>
      }
    }

    @for (panelTemplate of projectedPanelTemplates; track panelTemplate.dsHeaderPanel) {
      <ng-container
        [ngTemplateOutlet]="panelTemplate.templateRef"
        [ngTemplateOutletContext]="panelContext(panelTemplate.dsHeaderPanel)"
      />
    }

    @if (showMenu && mobileMenuTemplate) {
      <ng-container
        [ngTemplateOutlet]="mobileMenuTemplate.templateRef"
        [ngTemplateOutletContext]="mobileMenuTemplateContext"
      />
    }

    @if (showSearch && showMobileSearchOverlay && openAction === 'search') {
      <div
        dsScroll
        class="ds-header__mobile-search-overlay"
        role="dialog"
        aria-modal="true"
        [attr.aria-labelledby]="mobileSearchTitleId"
        cdkTrapFocus
        (keydown.escape)="closeMobileSearch()"
      >
        <div class="ds-header__mobile-search-header">
          <h2
            class="ds-header__mobile-search-title"
            [attr.id]="mobileSearchTitleId"
          >{{ mobileSearchTitle }}</h2>

          <ds-icon-button-v2
            class="ds-header__mobile-search-close"
            icon="Close"
            size="xs"
            emphasis="secondary"
            [ariaLabel]="mobileSearchCloseAriaLabel"
            (clicked)="closeMobileSearch()"
          />
        </div>

        <div class="ds-header__mobile-search-content">
          <ds-input-search
            #mobileSearchInput
            class="ds-header__mobile-search-input"
            [value]="searchValue"
            [expanded]="mobileSearchExpanded"
            [alwaysShowPanel]="true"
            panelAppearance="detached"
            label=""
            [ariaLabel]="searchAriaLabel"
            [placeholder]="searchPlaceholder"
            [suggestions]="searchSuggestions"
            [results]="searchResults"
            [showNoResults]="searchShowNoResults"
            [maxCharacters]="searchMaxCharacters"
            [showMaxCharacters]="false"
            [disabled]="searchDisabled"
            [clearLabel]="searchClearLabel"
            [suggestionsLabel]="searchSuggestionsLabel"
            [emptyTitle]="searchEmptyTitle"
            [emptyMessage]="searchEmptyMessage"
            (valueChange)="onSearchValueChange($event)"
            (expandedChange)="onMobileSearchExpandedChange($event)"
            (submitted)="onMobileSearchSubmit($event)"
            (cleared)="searchCleared.emit()"
            (suggestionSelected)="onMobileSearchSuggestionSelected($event)"
            (resultSelected)="onMobileSearchResultSelected($event)"
          />
        </div>
      </div>
    }
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      container: ds-header / inline-size;
      --ds-content-sheet-title-font-size: var(--ds-header-panel-title-font-size);
      --ds-content-sheet-title-font-weight: var(--ds-header-panel-title-font-weight);
      --ds-content-sheet-title-line-height: var(--ds-header-panel-title-line-height);
    }

    .ds-header {
      position: relative;
      display: flex;
      align-items: center;
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
      height: var(--ds-header-height);
      padding: var(--ds-header-padding);
      gap: var(--ds-header-content-gap);
      border: 0;
      border-bottom: var(--ds-header-border-width) solid var(--ds-header-border-color);
      background: var(--ds-header-background);
    }

    .ds-header--panel-open {
      z-index: var(--ds-header-overlay-z-index);
    }

    .ds-header__logo {
      display: inline-flex;
      flex: 0 0 auto;
    }

    .ds-header__tools {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: var(--ds-header-tools-gap);
      flex: 1 1 0;
      min-width: 0;
    }

    .ds-header__search {
      position: relative;
      width: 100%;
      max-width: var(--ds-header-search-width);
      height: var(--ds-header-search-height);
      flex: 1 1 0;
      min-width: 0;
      z-index: var(--ds-header-overlay-z-index);
    }

    .ds-header__search-component {
      --ds-input-search-background: var(--ds-header-search-background);
      --ds-input-search-border-color: var(--ds-header-search-border-color);
      --ds-input-search-border-radius: var(--ds-header-search-border-radius);
      --ds-input-search-border-width: var(--ds-header-search-border-width);
      --ds-input-search-field-font-size: var(--ds-header-search-font-size);
      --ds-input-search-field-font-weight: var(--ds-header-search-font-weight);
      --ds-input-search-field-height: var(--ds-header-search-height);
      --ds-input-search-field-line-height: var(--ds-header-search-line-height);
      --ds-input-search-icon-gap: var(--ds-header-search-gap);
      --ds-input-search-icon-color: var(--ds-header-search-icon-color);
      --ds-input-search-icon-size: var(--ds-header-search-icon-size);
      --ds-input-search-padding-x: var(--ds-header-search-padding-x);
      --ds-input-search-padding-y: var(--ds-header-search-padding-y);
      --ds-input-search-placeholder-color: var(--ds-header-search-placeholder-color);
      --ds-input-search-value-color: var(--ds-header-search-value-color);
      position: absolute;
      inset: 0;
      display: block;
      width: 100%;
      min-width: 0;
      max-width: 100%;
    }

    .ds-header__search-backdrop {
      position: fixed;
      top: var(--ds-header-height);
      right: 0;
      bottom: 0;
      left: 0;
      z-index: var(--ds-header-search-overlay-z-index);
      background: var(--ds-header-search-overlay);
      backdrop-filter: blur(var(--ds-header-backdrop-blur));
      -webkit-backdrop-filter: blur(var(--ds-header-backdrop-blur));
      animation: ds-header-search-backdrop-enter var(--ds-header-transition-duration) ease-out;
    }

    @keyframes ds-header-search-backdrop-enter {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .ds-header__actions {
      display: flex;
      align-items: center;
      gap: var(--ds-header-actions-gap);
      flex: 0 0 auto;
    }

    .ds-header__mobile-only {
      display: none;
    }

    .ds-header__mobile-search-overlay {
      position: fixed;
      inset: 0;
      z-index: var(--ds-header-overlay-z-index);
      display: none;
      flex-direction: column;
      align-items: stretch;
      box-sizing: border-box;
      overflow-y: auto;
      overscroll-behavior: contain;
      background: var(--ds-header-background);
      animation: ds-header-mobile-search-enter var(--ds-header-transition-duration) ease-out both;
    }

    @keyframes ds-header-mobile-search-enter {
      from { transform: translateX(100%); }
      to { transform: translateX(0); }
    }

    .ds-header__mobile-search-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      box-sizing: border-box;
      padding: var(--ds-header-padding);
    }

    .ds-header__mobile-search-title {
      min-width: 0;
      margin: 0;
      color: var(--ds-header-mobile-search-title-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-header-mobile-search-title-font-size);
      font-weight: var(--ds-header-mobile-search-title-font-weight);
      line-height: var(--ds-header-mobile-search-title-line-height);
    }

    .ds-header__mobile-search-content {
      display: flex;
      width: 100%;
      box-sizing: border-box;
      padding: 0 var(--ds-header-padding) var(--ds-header-padding);
    }

    .ds-header__mobile-search-input {
      width: 100%;
      min-width: 0;
      max-width: none;
    }

    .ds-header__mobile-search-close {
      flex: 0 0 auto;
    }

    @media (prefers-reduced-motion: reduce) {
      .ds-header__mobile-search-overlay {
        animation: none;
      }
    }

    .ds-header__item-sheet {
      --ds-header-contact-sheet-width: var(--ds-content-sheet-anchored-width);
      --ds-content-sheet-anchored-gap: var(--ds-header-panel-offset-y);
      --ds-content-sheet-anchored-max-height: 100vh;
    }

    /*
     * Every dropdown anchored to a header action — whether it's one of the
     * built-in panels above or a full recipe the application projects via
     * dsHeaderPanel — renders a ds-content-sheet inside this component's own
     * view tree. Targeting its anchored panel directly (not just an ancestor
     * wrapper) means this value wins over anything an application declares on
     * its own sheet, so the safe 16px inset each side is fixed here and stays
     * out of the application's control; only the max-width stays app-owned.
     */
    :host ::ng-deep .ds-content-sheet__sheet--anchored {
      --ds-content-sheet-viewport-margin: var(--ds-header-panel-viewport-margin, 24px) !important;
    }

    @container ds-header (max-width: 1140px) {
      .ds-header__item-sheet {
        --ds-header-contact-sheet-width: var(--ds-content-sheet-anchored-compact-width);
      }
    }

    /* Foundation breakpoint applied to the Header's own inline size. */
    @container ds-header (max-width: 839px) {
      .ds-header__tools {
        gap: 0;
      }

      .ds-header__search,
      .ds-header__search-backdrop,
      .ds-header__desktop-only-action,
      .ds-header__item-sheet--desktop-only {
        display: none;
      }

      .ds-header__mobile-only {
        display: inline-flex;
      }

      .ds-header__mobile-search-overlay {
        display: flex;
      }

      .ds-header__actions {
        gap: var(--ds-header-mobile-actions-gap);
      }
    }
  `],
})
export class DsHeaderComponent implements AfterViewInit {
  private static counter = 0;

  readonly mobileSearchTitleId = `ds-header-mobile-search-title-${++DsHeaderComponent.counter}`;

  @Input() logoAlt = 'AFP Capital';
  @Input() logoSize: DsLogoSize = 'm';
  @Input({ transform: booleanAttribute }) logoInvert = false;
  @Input() logoHref = '';
  @Input() logoTarget = '';
  @Input() logoRel = '';
  @Input() searchValue = '';
  @Input() searchPlaceholder = '¿Qué estás buscando?';
  @Input() searchAriaLabel = 'Buscar';
  @Input() searchSuggestions: string[] = [];
  @Input() searchResults: DsInputSearchResult[] = [];
  @Input() searchShowNoResults: boolean | null = null;
  @Input() searchMaxCharacters: number | null = null;
  @Input() searchClearLabel = 'Borrar';
  @Input() searchSuggestionsLabel = 'Sugerencias';
  @Input() searchEmptyTitle = 'No encontramos resultados';
  @Input() searchEmptyMessage = 'Por favor, intenta buscar de otra forma';
  @Input({ transform: booleanAttribute }) searchExpanded = false;
  @Input({ transform: booleanAttribute }) searchDisabled = false;
  @Input() actionsAriaLabel = 'Acciones de usuario';
  /** Application-defined actions. Pass null only to activate the deprecated three-item fallback. */
  @Input() items: readonly DsHeaderItem[] | null = [];
  @Input() defaultPanelMessage = 'Sin contenido configurado.';
  @Input() contactLabel = 'Contáctanos';
  @Input() contactAriaLabel = 'Contáctanos';
  @Input() notificationsLabel = 'Notificaciones';
  @Input() notificationsAriaLabel = 'Notificaciones';
  @Input() notificationsPanelTitle = 'Notificaciones';
  @Input() notificationsPanelAriaLabel = 'Notificaciones';
  @Input() notificationsPanelMessage = 'No tienes notificaciones nuevas.';
  @Input() profileLabel = 'Mi Perfil';
  @Input() profileAriaLabel = 'Mi Perfil';
  @Input() profilePanelAriaLabel = 'Mi perfil';
  @Input() menuLabel = 'Menú';
  @Input() menuAriaLabel = 'Abrir menú';
  @Input() profileInitials = 'FC';
  @Input() profilePanelTitle = 'Mi Perfil';
  @Input() profilePanelMessage = 'Aquí encontrarás las opciones de tu perfil.';
  @Input() profileCloseAriaLabel = 'Cerrar Mi perfil';
  @Input() mobileSearchTitle = 'Buscador';
  @Input() mobileSearchCloseAriaLabel = 'Cerrar buscador';
  @Input() selectedAction: DsHeaderAction | null = null;
  @Input() openAction: DsHeaderAction | null = null;
  @Input() contactPanelTitle = 'Contáctanos';
  @Input() contactPanelMessage = 'Aquí encontrarás nuestros canales de contacto.';
  @Input({ transform: booleanAttribute }) showSearch = true;
  @Input({ transform: booleanAttribute }) showContact = true;
  @Input({ transform: booleanAttribute }) showNotifications = true;
  @Input({ transform: booleanAttribute }) showProfile = true;
  @Input({ transform: booleanAttribute }) showMenu = true;
  @Input({ transform: booleanAttribute }) showContactPanel = true;
  @Input({ transform: booleanAttribute }) showNotificationsPanel = true;
  @Input({ transform: booleanAttribute }) showProfilePanel = true;
  @Input({ transform: booleanAttribute }) showMobileSearchOverlay = true;

  @Output() searchValueChange = new EventEmitter<string>();
  /** Lets the consuming application decide the logo action (usually navigation to home). */
  @Output() logoClicked = new EventEmitter<MouseEvent>();
  @Output() searchExpandedChange = new EventEmitter<boolean>();
  @Output() searchSubmitted = new EventEmitter<string>();
  @Output() searchCleared = new EventEmitter<void>();
  @Output() searchSuggestionSelected = new EventEmitter<string>();
  @Output() searchResultSelected = new EventEmitter<DsInputSearchResult>();
  @Output() searchClicked = new EventEmitter<MouseEvent>();
  @Output() contactClicked = new EventEmitter<MouseEvent>();
  @Output() notificationsClicked = new EventEmitter<MouseEvent>();
  @Output() profileClicked = new EventEmitter<MouseEvent>();
  @Output() itemSelected = new EventEmitter<DsHeaderItemSelection>();
  @Output() menuClicked = new EventEmitter<MouseEvent>();
  @Output() openActionChange = new EventEmitter<DsHeaderAction | null>();
  @ContentChild(DsHeaderContactContentDirective)
  contactContentTemplate?: DsHeaderContactContentDirective;
  @ContentChild(DsHeaderContactFooterDirective)
  contactFooterTemplate?: DsHeaderContactFooterDirective;
  @ContentChild(DsHeaderProfileContentDirective)
  profileContentTemplate?: DsHeaderProfileContentDirective;
  @ContentChild(DsHeaderProfileFooterDirective)
  profileFooterTemplate?: DsHeaderProfileFooterDirective;
  @ContentChildren(DsHeaderPanelDirective)
  panelTemplates?: QueryList<DsHeaderPanelDirective>;
  @ContentChildren(DsHeaderPanelContentDirective)
  managedPanelContentTemplates?: QueryList<DsHeaderPanelContentDirective>;
  @ContentChildren(DsHeaderPanelHeaderDirective)
  managedPanelHeaderTemplates?: QueryList<DsHeaderPanelHeaderDirective>;
  @ContentChildren(DsHeaderPanelFooterDirective)
  managedPanelFooterTemplates?: QueryList<DsHeaderPanelFooterDirective>;
  @ContentChild(DsHeaderMobileMenuDirective)
  mobileMenuTemplate?: DsHeaderMobileMenuDirective;

  @ViewChild('headerElement') private headerElement?: ElementRef<HTMLElement>;
  @ViewChildren('itemAnchor', { read: ElementRef }) private itemAnchors?: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('mobileSearchAnchor', { read: ElementRef }) private mobileSearchAnchor?: ElementRef<HTMLElement>;
  @ViewChild('mobileMenuAnchor', { read: ElementRef }) private mobileMenuAnchor?: ElementRef<HTMLElement>;
  @ViewChild('mobileSearchInput') private mobileSearchInput?: DsInputSearchComponent;

  mobileSearchExpanded = false;

  private readonly cdr = inject(ChangeDetectorRef);

  readonly panelTemplateContext: DsHeaderLegacyPanelTemplateContext = {
    close: () => this.closePanel(true),
  };

  /**
   * Custom items replace the legacy actions completely. The fallback only
   * exists so current consumers can migrate without an abrupt runtime change.
   */
  get resolvedItems(): readonly DsHeaderItem[] {
    if (this.items !== null) return this.items;

    const legacyItems: DsHeaderItem[] = [];
    if (this.showContact) {
      legacyItems.push({
        value: 'contact',
        label: this.contactLabel,
        ariaLabel: this.contactAriaLabel,
        icon: 'Forum',
        hideOnMobile: true,
        opensPanel: this.showContactPanel,
        panelTitle: this.contactPanelTitle,
        panelMessage: this.contactPanelMessage,
        panelMaxWidth: this.contactPanelWidth,
      });
    }
    if (this.showNotifications) {
      legacyItems.push({
        value: 'notifications',
        label: this.notificationsLabel,
        ariaLabel: this.notificationsAriaLabel,
        icon: 'Notifications',
        opensPanel: this.showNotificationsPanel,
        panelTitle: this.notificationsPanelTitle,
        panelAriaLabel: this.notificationsPanelAriaLabel,
        panelMessage: this.notificationsPanelMessage,
      });
    }
    if (this.showProfile) {
      legacyItems.push({
        value: 'profile',
        label: this.profileLabel,
        ariaLabel: this.profileAriaLabel,
        visual: 'avatar',
        initials: this.profileInitials,
        opensPanel: this.showProfilePanel,
        panelTitle: this.profilePanelTitle,
        panelAriaLabel: this.profilePanelAriaLabel,
        panelMessage: this.profilePanelMessage,
        panelMaxWidth: this.profilePanelWidth,
      });
    }
    return legacyItems;
  }

  legacyContentTemplateFor(
    value: string,
  ): DsHeaderContactContentDirective | DsHeaderProfileContentDirective | undefined {
    if (value === 'contact') return this.contactContentTemplate;
    if (value === 'profile') return this.profileContentTemplate;
    return undefined;
  }

  legacyFooterTemplateFor(
    value: string,
  ): DsHeaderContactFooterDirective | DsHeaderProfileFooterDirective | undefined {
    if (value === 'contact') return this.contactFooterTemplate;
    if (value === 'profile') return this.profileFooterTemplate;
    return undefined;
  }

  get projectedPanelTemplates(): readonly DsHeaderPanelDirective[] {
    const uniquePanels = new Map<DsHeaderPanelAction, DsHeaderPanelDirective>();
    for (const panel of this.panelTemplates?.toArray() ?? []) {
      if (!uniquePanels.has(panel.dsHeaderPanel)) {
        uniquePanels.set(panel.dsHeaderPanel, panel);
      }
    }
    return [...uniquePanels.values()];
  }

  readonly contentSheetDefaultWidth = 'var(--ds-content-sheet-default-max-width)';

  managedPanelContentFor(
    action: DsHeaderPanelAction,
  ): DsHeaderPanelContentDirective | undefined {
    return this.managedPanelContentTemplates
      ?.toArray()
      .find((template) => template.dsHeaderPanelContent === action);
  }

  managedPanelHeaderFor(
    action: DsHeaderPanelAction,
  ): DsHeaderPanelHeaderDirective | undefined {
    return this.managedPanelHeaderTemplates
      ?.toArray()
      .find((template) => template.dsHeaderPanelHeader === action);
  }

  managedPanelFooterFor(
    action: DsHeaderPanelAction,
  ): DsHeaderPanelFooterDirective | undefined {
    return this.managedPanelFooterTemplates
      ?.toArray()
      .find((template) => template.dsHeaderPanelFooter === action);
  }

  managedPanelContext(action: DsHeaderPanelAction): DsHeaderManagedPanelTemplateContext {
    const context = {
      action,
      close: () => this.closePanel(true),
    } satisfies Omit<DsHeaderManagedPanelTemplateContext, '$implicit'>;

    return {
      ...context,
      $implicit: context,
    };
  }

  managedPanelTitle(action: DsHeaderPanelAction, fallback: string): string {
    return this.managedPanelContentFor(action)?.title ?? fallback;
  }

  managedPanelDescriptiveText(action: DsHeaderPanelAction): string {
    return this.managedPanelContentFor(action)?.descriptiveText ?? '';
  }

  managedPanelAriaLabel(action: DsHeaderPanelAction, fallback: string): string {
    return this.managedPanelContentFor(action)?.ariaLabel ?? fallback;
  }

  managedPanelMaxWidth(
    action: DsHeaderPanelAction,
    fallback: number | string,
  ): number | string {
    return this.managedPanelContentFor(action)?.maxWidth ?? fallback;
  }

  managedPanelDismissOnBackdropClick(action: DsHeaderPanelAction): boolean {
    return this.managedPanelContentFor(action)?.dismissOnBackdropClick ?? true;
  }

  managedPanelShowCloseButton(action: DsHeaderPanelAction): boolean {
    return this.managedPanelContentFor(action)?.showCloseButton ?? true;
  }

  managedPanelShowBackButton(action: DsHeaderPanelAction): boolean {
    return this.managedPanelContentFor(action)?.showBackButton ?? false;
  }

  managedPanelTruncateTitle(action: DsHeaderPanelAction): boolean {
    return this.managedPanelContentFor(action)?.truncateTitle ?? false;
  }

  managedPanelShadow(
    action: DsHeaderPanelAction,
  ): DsHeaderPanelContentDirective['shadow'] {
    return this.managedPanelContentFor(action)?.shadow ?? 'l2';
  }

  managedPanelCloseAriaLabel(
    action: DsHeaderPanelAction,
    fallback = 'Cerrar',
  ): string {
    return this.managedPanelContentFor(action)?.closeAriaLabel ?? fallback;
  }

  managedPanelBackAriaLabel(action: DsHeaderPanelAction): string {
    return this.managedPanelContentFor(action)?.backAriaLabel ?? 'Volver';
  }

  emitManagedPanelBacked(action: DsHeaderPanelAction): void {
    this.managedPanelContentFor(action)?.backed.emit();
  }

  panelContext(action: DsHeaderPanelAction): DsHeaderPanelTemplateContext {
    const context = {
      action,
      open: this.isProjectedPanelOpen(action),
      anchor: this.panelAnchorElement(action),
      overlayBoundary: this.headerBoundaryElement,
      close: () => this.closeProjectedPanel(action),
    } satisfies Omit<DsHeaderPanelTemplateContext, '$implicit'>;

    return {
      ...context,
      $implicit: context,
    };
  }

  get mobileMenuTemplateContext(): DsHeaderMobileMenuTemplateContext {
    return {
      open: this.openAction === 'menu',
      close: () => this.closeMobileMenu(),
    };
  }

  ngAfterViewInit(): void {
    if (this.toPanelAction(this.openAction)) {
      this.cdr.detectChanges();
    }
    if (this.openAction === 'search') {
      this.cdr.detectChanges();
      this.mobileSearchInput?.focusInput();
    }
  }

  actionState(action: string): DsHeaderButtonState {
    return (this.openAction ?? this.selectedAction) === action ? 'selected' : 'default';
  }

  get contactAnchorElement(): HTMLElement | null {
    return this.panelAnchorElement('contact');
  }

  get profileAnchorElement(): HTMLElement | null {
    return this.panelAnchorElement('profile');
  }

  get notificationsAnchorElement(): HTMLElement | null {
    return this.panelAnchorElement('notifications');
  }

  get headerBoundaryElement(): HTMLElement | null {
    return this.headerElement?.nativeElement ?? null;
  }

  get mobileSearchAnchorElement(): HTMLElement | null {
    return this.mobileSearchAnchor?.nativeElement ?? null;
  }

  get contactPanelWidth(): string {
    return 'var(--ds-header-contact-sheet-width)';
  }

  get profilePanelWidth(): string {
    return 'var(--ds-header-profile-panel-width)';
  }

  get isPanelOpen(): boolean {
    return this.toPanelAction(this.openAction) !== null
      || (this.showSearch && this.searchExpanded);
  }

  hasProjectedPanel(action: DsHeaderPanelAction): boolean {
    return this.projectedPanelTemplates.some((panel) => panel.dsHeaderPanel === action);
  }

  onItemClicked(item: DsHeaderItem, event: MouseEvent): void {
    if (this.searchExpanded) {
      this.onSearchExpandedChange(false);
    }

    if (item.opensPanel !== false) {
      this.setOpenAction(this.openAction === item.value ? null : item.value);
    } else if (this.openAction !== null) {
      this.setOpenAction(null);
    }

    this.itemSelected.emit({ item, value: item.value, event });
    switch (item.value) {
      case 'contact': this.contactClicked.emit(event); break;
      case 'notifications': this.notificationsClicked.emit(event); break;
      case 'profile': this.profileClicked.emit(event); break;
      default: break;
    }
  }

  onSystemActionClicked(action: 'search' | 'menu', event: MouseEvent): void {
    if (this.searchExpanded) {
      this.onSearchExpandedChange(false);
    }

    if (action === 'search' && !this.showMobileSearchOverlay) {
      if (this.openAction !== null) this.setOpenAction(null);
      this.searchClicked.emit(event);
      return;
    }

    const opening = this.openAction !== action;
    this.setOpenAction(opening ? action : null);

    switch (action) {
      case 'search':
        this.searchClicked.emit(event);
        if (opening) {
          this.cdr.detectChanges();
          this.mobileSearchInput?.focusInput();
        } else {
          this.mobileSearchExpanded = false;
        }
        break;
      case 'menu': this.menuClicked.emit(event); break;
    }
  }

  closePanel(restoreFocus = false): void {
    const action = this.toPanelAction(this.openAction);
    const anchor = action ? this.panelAnchorElement(action) : null;
    this.setOpenAction(null);
    if (restoreFocus) {
      void Promise.resolve().then(() => {
        anchor?.querySelector<HTMLButtonElement>('button')?.focus();
      });
    }
  }

  /**
   * Content Sheet's `dismissed` now fires whenever it's closed for any
   * reason, including a sibling header action stealing `openAction` out
   * from under it. Guard on the action so a dismiss that arrives after this
   * panel has already lost focus to another one doesn't close that other
   * panel instead.
   */
  closeManagedPanel(action: DsHeaderAction, restoreFocus = false): void {
    if (this.openAction !== action) return;
    const anchor = this.panelAnchorElement(action);
    this.setOpenAction(null);
    if (restoreFocus) {
      void Promise.resolve().then(() => {
        anchor?.querySelector<HTMLButtonElement>('button')?.focus();
      });
    }
  }

  closeProjectedPanel(action: DsHeaderPanelAction, restoreFocus = true): void {
    if (this.openAction !== action) return;
    const anchor = this.panelAnchorElement(action);
    this.setOpenAction(null);
    if (restoreFocus) {
      void Promise.resolve().then(() => {
        anchor?.querySelector<HTMLButtonElement>('button')?.focus();
      });
    }
  }

  closeMobileSearch(): void {
    if (this.openAction !== 'search') return;
    this.setOpenAction(null);
    this.mobileSearchExpanded = false;
    this.mobileSearchAnchorElement?.querySelector<HTMLButtonElement>('button')?.focus();
  }

  closeMobileMenu(): void {
    if (this.openAction !== 'menu') return;
    this.setOpenAction(null);
    void Promise.resolve().then(() => {
      this.mobileMenuAnchor?.nativeElement
        .querySelector<HTMLButtonElement>('button')
        ?.focus();
    });
  }

  onMobileSearchExpandedChange(expanded: boolean): void {
    this.mobileSearchExpanded = expanded;
  }

  onMobileSearchSubmit(value: string): void {
    this.searchSubmitted.emit(value);
    this.closeMobileSearch();
  }

  onMobileSearchResultSelected(result: DsInputSearchResult): void {
    this.searchResultSelected.emit(result);
    this.closeMobileSearch();
  }

  onMobileSearchSuggestionSelected(suggestion: string): void {
    this.searchSuggestionSelected.emit(suggestion);
    this.closeMobileSearch();
  }

  onSearchValueChange(value: string): void {
    this.searchValue = value;
    this.searchValueChange.emit(value);
  }

  onSearchExpandedChange(expanded: boolean): void {
    if (expanded && this.openAction !== null) {
      this.setOpenAction(null);
    }
    this.searchExpanded = expanded;
    this.searchExpandedChange.emit(expanded);
  }

  onSearchSubmit(value: string): void {
    this.searchSubmitted.emit(value);
  }

  private setOpenAction(action: DsHeaderAction | null): void {
    this.openAction = action;
    this.openActionChange.emit(action);
  }

  private isProjectedPanelOpen(action: DsHeaderPanelAction): boolean {
    return this.hasProjectedPanel(action) && this.isPanelActionOpen(action);
  }

  private isPanelActionOpen(action: DsHeaderPanelAction): boolean {
    const item = this.resolvedItems.find((candidate) => candidate.value === action);
    return this.openAction === action && item?.opensPanel !== false;
  }

  panelAnchorElement(action: DsHeaderPanelAction): HTMLElement | null {
    const index = this.resolvedItems.findIndex((item) => item.value === action);
    return index < 0 ? null : this.itemAnchors?.get(index)?.nativeElement ?? null;
  }

  private toPanelAction(action: DsHeaderAction | null): DsHeaderPanelAction | null {
    if (action === null) return null;
    const item = this.resolvedItems.find((candidate) => candidate.value === action);
    return item?.opensPanel !== false ? item?.value ?? null : null;
  }
}
